import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import { authApi } from '../services/api';
import { isAuthenticated, tokenStorage } from '../services/apiClient';
import { LoginCredentials, RegisterCredentials, SocialLoginPayload, SocialProvider } from '../types/api';

export const CURRENT_USER_KEY = ['currentUser'] as const;

/** The signed-in user's profile (GET /auth/user/). */
export const useCurrentUser = () =>
  useQuery({ queryKey: CURRENT_USER_KEY, queryFn: authApi.me, enabled: isAuthenticated() });

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes: { full_name: string }) => authApi.updateProfile(changes),
    onSuccess: (user) => queryClient.setQueryData(CURRENT_USER_KEY, user),
  });
};

/** Permanently deletes the account, then leaves for the login page. */
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (confirm: string) => authApi.deleteAccount(confirm),
    // Done here, not in a mutate() callback: the page unmounts on navigate.
    onSuccess: () => {
      navigate('/login', { replace: true });
      queryClient.clear();
      showToast('Your account has been deleted.', 'success');
    },
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
    onSuccess: () => queryClient.clear(),
  });
};

export const useRegister = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: RegisterCredentials) => authApi.register(credentials),
    onSuccess: () => queryClient.clear(),
  });
};

// The provider popup runs in the page's click handler (browsers block popups
// opened later); this sends the credential it returned to the backend.
export const useSocialLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, payload }: { provider: SocialProvider; payload: SocialLoginPayload }) =>
      authApi.socialLogin(provider, payload),
    onSuccess: () => queryClient.clear(),
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => authApi.logout(),
    // Even if the server call fails, tokens are cleared locally — always leave.
    onSettled: () => {
      tokenStorage.clear();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });
};
